import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Box, useMediaQuery } from '@chakra-ui/react';
import "./App.css";
import AuthContext, { AuthProvider } from './components/services/AuthContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { SidebarProvider } from './components/utils/SidebarContext';
import { Text, Button, Image, Flex, VStack } from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
// Import route protection components
import {
  AdminRoute,
  InstructorRoute,
  StudentRoute,
  AdminInstructorRoute,
  AuthenticatedRoute,
  PublicRoute,
} from './components/shared/ProtectedRoute';

// Import all your pages
import LoginPage from './components/pages/LoginPage';
import ForgotPasswordPage from './components/pages/ForgotPasswordPage';
import ResetPasswordPage from './components/pages/ResetPasswordPage';

// Public pages
import HomePage from './components/pages/HomePage';
import PacksPage from './components/pages/PacksPage';
import AboutPage from './components/pages/AboutPage';
import VisionPage from './components/pages/VisionPage';
import ContactPage from './components/pages/ContactPage';
import CourseDiscovery from './components/pages/CourseDiscovery';
import NiveauDetails from './components/pages/NiveauDetails';

// Role-based dashboards
import AdminDashboard from './components/pages/AdminDashboard';
import AdminStudentFeedback from './components/pages/AdminStudentFeedback';
import InstructorDashboard from './components/pages/InstructorDashboard';
import StudentDashboard from './components/pages/StudentDashboard';

// Admin pages
import UserAdminManagement from './components/pages/UserAdminManagement';
import MaylawanAdmin from './components/pages/MaylawanAdmin.jsx';
import CourseAdminManagement from './components/pages/CourseAdminManagement';
import EditierAdminCourse from './components/pages/EditierAdminCourse';
import AdminEvents from './components/pages/AdminEvent';
import NiveauManagement from './components/pages/NiveauManagement';
import RegistrationManagement from './components/pages/RegistrationManagement';
import AdminPayments from './pages/AdminPayments';
import BacReviewRegistrations from './components/pages/BacReviewRegistrations';

// Instructor pages
import QCMManagementPage from './components/pages/QCMManagementPage';

// Student pages
import Courses from './components/pages/Courses';
import CourseDetails from './components/pages/CourseDetails';
import QCMList from './components/pages/QCMList';
import QCMTestPage from './components/pages/QCMTestPage';
import StudentQCMPage from './components/pages/StudentQCMPage';
import UserUploadsPage from './components/pages/UserUploadsPage';
import AttendancePopup from './components/shared/AttendancePopup';
import AttendanceControl from './components/pages/AttendanceControl';

// Shared pages
import Events from './components/pages/Events';
import Notifications from './components/pages/Notifications';
import Reclamations from './components/pages/Reclamations';

// Legacy dashboard (for backward compatibility)
import InstructorEvents from './components/pages/InstructorEvents';
import InstructorFileUpload from './components/pages/InstructorFileUpload';
import CourseDetailsInstructor from './components/pages/CourseDetailsInstructor';
import UnsupportedBrowser from './components/shared/UnsupportedBrowser';
import InstructorCourseManagement from './components/pages/InstructorCoursemangement';
import AdminReclamationsManager from './components/pages/AdminReclamationsManager';
import InstructorCourses from './components/pages/InstructorCourses';
import WhatsAppNamesPage from './components/pages/WhatsAppNamesPage';
import AbsenceManagement from './components/pages/AdminAlert';
import PDFViewer from './components/pages/pdfviewer';
import PublicVideoPage from './components/pages/PublicVideoPage';
import RecruitmentFormPage from './components/pages/RecruitmentFormPage';
import AdminRecruitmentManagement from './components/pages/AdminRecruitmentManagement';
import AdminScheduleForm from './components/pages/AdminScheduleForm';
import ScheduleFormPublic from './components/pages/ScheduleFormPublic';
import SurveyCheck from './components/shared/SurveyCheck';
import BacReviewCheck from './components/shared/BacReviewCheck';
import GicLocationCheck from './components/shared/GicLocationCheck';
import AdminSurveyManagement from './components/pages/AdminSurveyManagement';
import MeetingRoom from './components/pages/MeetingRoom';
import MeetingsPage from './components/pages/MeetingsPage';



const App = () => {
  const [isSupported, setIsSupported] = React.useState(true);
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [isFlutterWebView, setIsFlutterWebView] = React.useState(false);
  const { loading } = useContext(AuthContext);

  React.useEffect(() => {
    setIsSupported(isSupportedBrowser());
    setIsFlutterWebView(isAccessedFromFlutterWebView());
  }, []);

  // Check if the current path is the public video page or recruitment page
  const isPublicExemptPage = ['/video', '/recrutement'].includes(window.location.pathname)
    || window.location.pathname.startsWith('/schedule');

  // Check if accessed from mobile but not from Flutter WebView
  // Exception: Allow specified public routes without restriction
  if (isMobile && !isFlutterWebView && !isPublicExemptPage) {
    return (
      <Flex
        height="100vh"
        align="center"
        justify="center"
        p={4}
        textAlign="center"
      >
        <VStack spacing={6}>
          <Image
            src="https://upload.wikimedia.org/wikipedia/commons/6/64/Android_logo_2019_%28stacked%29.svg"
            alt="Android App"
            boxSize="100px"
            mb={4}
          />

          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            هذه المنصة متاحة فقط من خلال التطبيق المخصص
          </Text>


          <Button
            as="a"
            href="https://api.cobra-education.com/uploads/apps/Elcobra.apk" // Replace with your actual app download link
            target="_blank"
            colorScheme="green" // Android green color
            leftIcon={<DownloadIcon />}
            size="lg"
            px={8}
            py={4}
            borderRadius="md"
            _hover={{
              transform: "scale(1.05)",
              boxShadow: "lg"
            }}
            transition="all 0.2s"
          >
            Download App Now
          </Button>


        </VStack>
      </Flex>
    );
  }

  if (loading) {
    return (
      <Box height="100vh" display="flex" alignItems="center" justifyContent="center">
        <Text>جاري التحميل...</Text>
      </Box>
    );
  }

  if (!isSupported) {
    return <UnsupportedBrowser />;
  }

  return (
    <div id="app-container">
      <AttendancePopup />
      {/* <ScheduleFormPopup /> */}
      <SurveyCheck />
      <BacReviewCheck />
      <GicLocationCheck />
      <AnimatePresence>
        <Box>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <PublicRoute>
                <HomePage />
              </PublicRoute>
            } />

            <Route path="/login" element={
              <PublicRoute redirectIfAuthenticated={true}>
                <LoginPage />
              </PublicRoute>
            } />

            <Route path="/forgot-password" element={
              <PublicRoute redirectIfAuthenticated={true}>
                <ForgotPasswordPage />
              </PublicRoute>
            } />

            <Route path="/reset-password" element={
              <PublicRoute redirectIfAuthenticated={true}>
                <ResetPasswordPage />
              </PublicRoute>
            } />

            <Route path="/packs" element={
              <PublicRoute>
                <PacksPage />
              </PublicRoute>
            } />

            <Route path="/discover" element={
              <PublicRoute>
                <CourseDiscovery />
              </PublicRoute>
            } />

            <Route path="/discover/:id" element={
              <PublicRoute>
                <NiveauDetails />
              </PublicRoute>
            } />

            <Route path="/about" element={
              <PublicRoute>
                <AboutPage />
              </PublicRoute>
            } />

            <Route path="/vision" element={
              <PublicRoute>
                <VisionPage />
              </PublicRoute>
            } />

            <Route path="/contact" element={
              <PublicRoute>
                <ContactPage />
              </PublicRoute>
            } />

            <Route path="/video" element={
              <PublicRoute>
                <PublicVideoPage />
              </PublicRoute>
            } />

            <Route path="/recrutement" element={
              <PublicRoute>
                <RecruitmentFormPage />
              </PublicRoute>
            } />

            {/* Legacy Dashboard Route - Redirect based on role */}


            {/* Admin Routes */}


            <Route path="/admin/users" element={
              <AdminRoute>
                <UserAdminManagement />
              </AdminRoute>
            } />
            <Route path="/admin/maylawan" element={
              <AdminRoute>
                <MaylawanAdmin />
              </AdminRoute>
            } />
            <Route path="/admin/whatsapp-groups" element={
              <AdminRoute>
                <WhatsAppNamesPage />
              </AdminRoute>
            } />
            <Route path="/instructor/absencemanagement" element={
              <AdminRoute>
                <AbsenceManagement />
              </AdminRoute>
            } />
            <Route path="/attendance/control" element={
              <AdminInstructorRoute>
                <AttendanceControl />
              </AdminInstructorRoute>
            } />

            <Route path="/admin/users/create" element={
              <AdminRoute>
                <UserAdminManagement />
              </AdminRoute>
            } />

            <Route path="/admin/cours" element={
              <AdminRoute>
                <CourseAdminManagement />
              </AdminRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />

            <Route path="/admin/courses" element={
              <AdminRoute>
                <CourseAdminManagement />
              </AdminRoute>
            } />
            <Route path="/admin/reclamation" element={
              <AdminRoute>
                <AdminReclamationsManager />
              </AdminRoute>
            } />

            <Route path="/admin/courses/create" element={
              <AdminRoute>
                <CourseAdminManagement />
              </AdminRoute>
            } />

            <Route path="/admin/courses/:id" element={
              <AdminRoute>
                <EditierAdminCourse />
              </AdminRoute>
            } />
            <Route path="/instructor/courses/:id" element={
              <InstructorRoute>
                <InstructorCourses />
              </InstructorRoute>
            } />
            <Route path="/instructor/abmanagement" element={
              <InstructorRoute>
                <AbsenceManagement />
              </InstructorRoute>
            } />

            <Route path="/admin/events" element={
              <AdminRoute>
                <AdminEvents />
              </AdminRoute>
            } />
            <Route path="/pdf-viewer" element={<PDFViewer />} />
            <Route path="/admin/homepage" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />

            <Route path="/admin/statistics" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/student-feedback" element={
              <AdminRoute>
                <AdminStudentFeedback />
              </AdminRoute>
            } />

            <Route path="/admin/niveaux" element={
              <AdminRoute>
                <NiveauManagement />
              </AdminRoute>
            } />

            <Route path="/admin/registrations" element={
              <AdminRoute>
                <RegistrationManagement />
              </AdminRoute>
            } />

            <Route path="/admin/payments" element={
              <AdminRoute>
                <AdminPayments />
              </AdminRoute>
            } />

            <Route path="/admin/recrutement" element={
              <AdminRoute>
                <AdminRecruitmentManagement />
              </AdminRoute>
            } />

            <Route path="/admin/schedule-forms" element={
              <AdminRoute>
                <AdminScheduleForm />
              </AdminRoute>
            } />

            <Route path="/admin/surveys" element={
              <AdminRoute>
                <AdminSurveyManagement />
              </AdminRoute>
            } />

            <Route path="/admin/bac-review" element={
              <AdminRoute>
                <BacReviewRegistrations />
              </AdminRoute>
            } />

            <Route path="/schedule/:id" element={
              <PublicRoute>
                <ScheduleFormPublic />
              </PublicRoute>
            } />

            {/* Instructor Routes */}
            <Route path="/instructor" element={
              <InstructorRoute>
                <InstructorDashboard />
              </InstructorRoute>
            } />



            <Route path="/instructor/qcm" element={
              <InstructorRoute>
                <QCMManagementPage />
              </InstructorRoute>
            } />



            <Route path="/instructor/uploads" element={
              <InstructorRoute>
                <UserUploadsPage />
              </InstructorRoute>
            } />

            <Route path="/instructor/comments/:id" element={
              <InstructorRoute>
                <CourseDetailsInstructor />
              </InstructorRoute>
            } />
            <Route path="/instructor/courses" element={
              <InstructorRoute>
                <InstructorCourseManagement />
              </InstructorRoute>
            } />
            <Route path="/instructor" element={
              <InstructorRoute>
                <InstructorDashboard />
              </InstructorRoute>
            } />
            <Route path="/instructor/upload" element={
              <InstructorRoute>
                <InstructorFileUpload />
              </InstructorRoute>
            } />
            <Route path="/instructor/events" element={
              <InstructorRoute>
                <InstructorEvents />
              </InstructorRoute>
            } />


            {/* Student Routes */}

            <Route path="/student/courses" element={
              <StudentRoute>
                <Courses />
              </StudentRoute>
            } />

            <Route path="/student/courses/:id" element={
              <StudentRoute>
                <CourseDetails />
              </StudentRoute>
            } />
            <Route path="/student" element={
              <StudentRoute>
                <StudentDashboard />
              </StudentRoute>
            } />

            <Route path="/student/qcms" element={
              <StudentRoute>
                <QCMList />
              </StudentRoute>
            } />

            <Route path="/student/qcms/:id/take" element={
              <StudentRoute>
                <QCMTestPage />
              </StudentRoute>
            } />

            <Route path="/student/qcms/:id/result" element={
              <StudentRoute>
                <StudentQCMPage />
              </StudentRoute>
            } />

            <Route path="/student/uploads" element={
              <StudentRoute>
                <UserUploadsPage />
              </StudentRoute>
            } />

            <Route path="/student/notifications" element={
              <StudentRoute>
                <Notifications />
              </StudentRoute>
            } />

            <Route path="/student/reclamations" element={
              <StudentRoute>
                <Reclamations />
              </StudentRoute>
            } />

            {/* Shared Authenticated Routes */}
            <Route path="/events" element={
              <StudentRoute>
                <Events />
              </StudentRoute>
            } />

            <Route path="/notifications" element={
              <StudentRoute>
                <Notifications />
              </StudentRoute>
            } />

            <Route path="/reclamations" element={
              <StudentRoute>
                <Reclamations />
              </StudentRoute>
            } />

            {/* Live Meeting Routes */}
            <Route path="/meetings" element={
              <AuthenticatedRoute>
                <MeetingsPage />
              </AuthenticatedRoute>
            } />
            <Route path="/meet/:roomName" element={
              <AuthenticatedRoute>
                <MeetingRoom />
              </AuthenticatedRoute>
            } />

            {/* Legacy routes for backward compatibility */}
            <Route path="/courses" element={
              <StudentRoute>
                <Courses />
              </StudentRoute>
            } />

            <Route path="/courses/:id" element={
              <StudentRoute>
                <CourseDetails />
              </StudentRoute>
            } />

            <Route path="/qcm" element={
              <StudentRoute>
                <QCMList />
              </StudentRoute>
            } />

            <Route path="/qcm/:id/take" element={
              <StudentRoute>
                <QCMTestPage />
              </StudentRoute>
            } />

            <Route path="/uploads" element={
              <StudentRoute>
                <UserUploadsPage />
              </StudentRoute>
            } />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </AnimatePresence>
    </div>
  );
};


function isSupportedBrowser() {
  const userAgent = navigator.userAgent;
  const chromeMatch = userAgent.match(/Chrome\/([0-9]+)/);
  const chromeVersion = chromeMatch ? parseInt(chromeMatch[1], 10) : 0;

  // Check if it's Chrome and exactly version 135
  const isChrome135 = chromeMatch && chromeVersion >= 127;

  // Check if it's Edge or Firefox (and not Chrome 135)
  const isEdge = userAgent.includes("Edg/");
  const isFirefox = userAgent.includes("Firefox/");

  return isChrome135 && !isEdge && !isFirefox;
}

// Function to detect if the app is accessed from Flutter WebView
function isAccessedFromFlutterWebView() {
  const userAgent = navigator.userAgent;

  // Check for Flutter WebView specific user agent patterns
  // Flutter WebView typically includes "wv" (WebView) and "Flutter" in the user agent
  const isFlutterWebView = userAgent.includes('Flutter') ||
    userAgent.includes('FlutterWebView') ||
    userAgent.includes('MyFlutterApp'); // Custom identifier we'll add

  // Additional check for WebView indicators
  const isWebView = userAgent.includes('wv') ||
    (userAgent.includes('Version/') && userAgent.includes('Mobile'));

  // Check for custom header or parameter that we'll set from Flutter
  const hasFlutterIdentifier = window.location.search.includes('flutter=true') ||
    document.referrer.includes('flutter') ||
    sessionStorage.getItem('isFlutterApp') === 'true';

  return isFlutterWebView || hasFlutterIdentifier || isWebView;
}

export default function MainApp() {
  return (
    <AuthProvider>
      <TranslationProvider>
        <SidebarProvider>
          <App />
        </SidebarProvider>
      </TranslationProvider>
    </AuthProvider>
  );
}

